import type { AppContext } from '@application/context/AppContext';
import type { Tenant } from '@shared/types/Tenant';
import { getTenantProfile, saveTenantProfile } from '@application/persistence/TenantProfileService';
import { resolveDeploymentMode } from '@infra/licence/DataModeResolver';
import { logger } from '@infra/telemetry/Logger';
import { AuthError } from '@infra/auth/AuthService';

const DEMO_TENANT_ID = 'demo-nexxus-tenant';

/**
 * Enregistre la fiche entreprise si le rôle de la session le permet. Un profil sans droit d'écriture sur la fiche
 * (lecture, comptable, salarié) ouvre quand même l'application : la fiche reste en mémoire pour la session, c'est
 * l'administrateur qui la persiste à sa propre connexion. Sans cela, ces profils ne pouvaient pas démarrer sur un
 * navigateur vierge (« Démarrage interrompu »).
 */
async function persistTenantIfAllowed(ctx: AppContext, tenant: Tenant): Promise<boolean> {
  try {
    await saveTenantProfile(ctx, tenant);
    return true;
  } catch (err) {
    if (err instanceof AuthError && err.code === 'INSUFFICIENT_ROLE') {
      logger.info({ event: 'tenant_profile_not_persisted_role', tenantId: tenant.id });
      return false;
    }
    throw err;
  }
}

/**
 * Crée le tenant local à partir des claims du certificat d'activation
 * (licence créée via le dashboard admin).
 */
export class TenantProvisioningService {
  static isDemoTenant(tenantId: string | undefined): boolean {
    return tenantId === DEMO_TENANT_ID;
  }

  static async ensureTenantFromLicence(ctx: AppContext): Promise<string | null> {
    const runtime = ctx.getLicenceRuntime();
    const claims = runtime?.getState()?.claims;
    if (!claims?.tenant_id) return null;

    const tenantId = claims.tenant_id;
    const existing = await getTenantProfile(ctx, tenantId);
    const deployment = resolveDeploymentMode(claims);
    if (existing) {
      if (existing.mode_deploiement !== deployment || existing.deployment_mode !== deployment) {
        const updated = { ...existing, mode_deploiement: deployment, deployment_mode: deployment };
        await persistTenantIfAllowed(ctx, updated);
        ctx.tenantContext.setTenant(updated);
      } else {
        ctx.tenantContext.setTenant(existing);
      }
      return tenantId;
    }

    const now = new Date().toISOString();
    const tenant: Tenant = {
      id: tenantId,
      code: tenantId.replace(/^tnt-/, '').slice(0, 8).toUpperCase() || 'CLIENT',
      raison_sociale: 'Mon entreprise',
      nom: 'Mon entreprise',
      tier: claims.tier ?? 'standard',
      mode_deploiement: deployment,
      deployment_mode: deployment,
      forme_juridique: 'SARL',
      regime_fiscal: 'reel_normal',
      capital_social: 0,
      nif: '',
      rccm: '',
      adresse: '',
      date_creation: now.slice(0, 10),
      tier_history: [],
      periode_courante: now.slice(0, 7),
      exercice_courant: now.slice(0, 4),
      parametres: { devise_principale: 'XAF', exercice_debut_mois: 1 },
      settings: { devise: 'XAF', exercice_debut_mois: 1 },
      cree_le: now,
    };

    const persisted = await persistTenantIfAllowed(ctx, tenant);
    if (!persisted) ctx.tenantContext.setTenant(tenant);
    logger.info({ event: 'tenant_provisioned_from_licence', tenantId, persisted });
    return tenantId;
  }
}
